import { useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { IconChevronRight, MicroLabel } from "@bht/ui";
import { ProfileAvatarButton, ProfileDrawer } from "./ProfileDrawer.js";
import { ThemeToggle } from "./ThemeToggle.js";
import { FeedbackButton } from "./FeedbackWidget.js";

/**
 * Desktop layout per docs/PLAN-2026-05-26-ux-parity.md §2.4.5 and the
 * design system desktop kit (project/ui_kits/desktop/).
 *
 * Layout:
 *
 *   ┌────────────────────────── 56px top bar ──────────────────────────┐
 *   │ brand · BREAK·THE·TEST  ›  crumbs    ⌘K · 🔔 · avatar     │
 *   ├──────────┬─────────────────────────────────────────────┬─────────┤
 *   │ 240px    │ main content                                 │ 380px  │
 *   │ sidebar  │                                              │ right  │
 *   │ TRAIN    │                                              │ rail   │
 *   │ REVIEW   │                                              │ (opt)  │
 *   │ META     │                                              │        │
 *   └──────────┴─────────────────────────────────────────────┴─────────┘
 *
 * The right rail is opt-in via `rightRail` prop. Drill pages use it for
 * signature distribution + snap latency sparkline. Non-drill pages
 * (Home, Mistakes, Profile) leave it null and the main expands.
 *
 * `crumbs` is an optional breadcrumb chip array shown in the top bar.
 *
 * `hideSidebar` collapses the sidebar entirely — used in drill mode per
 * the design's "sidebar hidden during drill, full focus on the stage"
 * pattern.
 */
export type DesktopShellProps = {
  children: ReactNode;
  rightRail?: ReactNode;
  crumbs?: ReactNode[];
  hideSidebar?: boolean;
};

export function DesktopShell({
  children,
  rightRail,
  crumbs,
  hideSidebar = false,
}: DesktopShellProps) {
  const [profileOpen, setProfileOpen] = useState(false);
  const location = useLocation();
  const activeKey = activeNavKey(location.pathname);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        gridTemplateRows: "56px 1fr",
        gridTemplateColumns: hideSidebar ? "1fr" : "240px 1fr",
        gridTemplateAreas: hideSidebar
          ? `"topbar" "main"`
          : `"topbar topbar" "sidebar main"`,
        background: "var(--bg-0)",
      }}
    >
      <DesktopTopBar
        crumbs={crumbs}
        onProfileClick={() => setProfileOpen(true)}
      />
      {!hideSidebar && <DesktopSidebar activeKey={activeKey} />}
      <main
        style={{
          gridArea: "main",
          minWidth: 0,
          display: rightRail ? "grid" : "block",
          gridTemplateColumns: rightRail ? "1fr 380px" : undefined,
          overflow: "hidden",
        }}
      >
        <div style={{ overflowY: "auto", minWidth: 0, height: "100%" }}>{children}</div>
        {rightRail && (
          <aside
            style={{
              borderLeft: "1px solid var(--border-faint)",
              background: "var(--bg-1)",
              overflowY: "auto",
              padding: 20,
            }}
          >
            {rightRail}
          </aside>
        )}
      </main>

      <ProfileDrawer open={profileOpen} onClose={() => setProfileOpen(false)} />
    </div>
  );
}

// ───────────────── Top bar ─────────────────

function DesktopTopBar({
  crumbs,
  onProfileClick,
}: {
  crumbs?: ReactNode[];
  onProfileClick: () => void;
}) {
  return (
    <header
      style={{
        gridArea: "topbar",
        display: "flex",
        alignItems: "center",
        gap: 16,
        padding: "0 20px",
        borderBottom: "1px solid var(--border-line)",
        background: "var(--bg-1)",
      }}
    >
      <Link
        to="/app/home"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          color: "var(--fg-0)",
          textDecoration: "none",
        }}
      >
        <BrandTickSmall />
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 12,
            letterSpacing: "0.16em",
            color: "var(--fg-0)",
            fontWeight: 600,
          }}
        >
          BREAK·THE·TEST
        </span>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            letterSpacing: "0.16em",
            color: "var(--fg-3)",
          }}
        >
          · LEARNER
        </span>
      </Link>

      {crumbs && crumbs.length > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            paddingLeft: 14,
            borderLeft: "1px solid var(--border-line)",
            color: "var(--fg-2)",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
          }}
        >
          {crumbs.map((c, i) => (
            <span key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {i > 0 && (
                <IconChevronRight
                  size={12}
                  color="var(--fg-3)"
                  style={{ flexShrink: 0 }}
                />
              )}
              <span
                style={{
                  color: i === crumbs.length - 1 ? "var(--fg-0)" : "var(--fg-2)",
                }}
              >
                {c}
              </span>
            </span>
          ))}
        </div>
      )}

      <div style={{ flex: 1 }} />

      <FeedbackButton />
      <ThemeToggle size={18} />
      <ProfileAvatarButton onClick={onProfileClick} size={28} />
    </header>
  );
}

// ───────────────── Sidebar ─────────────────

type NavItem = {
  key: string;
  label: string;
  href: string;
  glyph: ReactNode;
  badge?: string;
};

type NavGroup = {
  group: string;
  items: NavItem[];
};

const NAV: NavGroup[] = [
  {
    group: "Train",
    items: [
      { key: "home", label: "Home", href: "/app/home", glyph: <TargetGlyph /> },
      { key: "plan", label: "Study plan", href: "/app/plan", glyph: <ClipboardGlyph /> },
      // Plural. Once this destination covers SAT, ACT and AP Calculus, the
      // singular label describes the architecture it used to have.
      { key: "practice-test", label: "Practice tests", href: "/app/practice-test", glyph: <ClipboardGlyph /> },
      { key: "packs", label: "Library", href: "/app/packs", glyph: <PackGlyph /> },
    ],
  },
  {
    group: "Review",
    items: [
      { key: "mistakes", label: "Mistake review", href: "/app/mistakes", glyph: <ReplayGlyph /> },
      { key: "mastery", label: "Mastery", href: "/app/mastery", glyph: <ChartGlyph /> },
    ],
  },
  {
    group: "More",
    items: [
      // Hidden Test also has its own card on Home; kept here so the same
      // destinations sit at the same level on desktop and mobile.
      { key: "hidden-test", label: "Hidden Test", href: "/app/hidden-test", glyph: <PlayGlyph /> },
      { key: "faq", label: "How it works", href: "/faq", glyph: <HelpGlyph /> },
      { key: "blog", label: "Journal", href: "/blog/", glyph: <JournalGlyph /> },
      { key: "vyzrly", label: "Vyzrly", href: "/app/vyzrly", glyph: <VyzrlyGlyph /> },
    ],
  },
];

function activeNavKey(pathname: string): string {
  if (pathname === "/app/home" || pathname === "/app" || pathname === "/app/") return "home";
  if (pathname.startsWith("/app/drill")) return "home";
  if (pathname.startsWith("/app/hidden-test")) return "hidden-test";
  if (pathname.startsWith("/app/practice-test")) return "practice-test";
  if (pathname.startsWith("/app/mistakes")) return "mistakes";
  if (pathname.startsWith("/app/mastery")) return "mastery";
  if (pathname.startsWith("/app/packs")) return "packs";
  if (pathname.startsWith("/blog")) return "blog";
  if (pathname.startsWith("/app/vyzrly")) return "vyzrly";
  if (pathname === "/faq" || pathname.startsWith("/faq")) return "faq";
  return "";
}

function DesktopSidebar({ activeKey }: { activeKey: string }) {
  return (
    <aside
      style={{
        gridArea: "sidebar",
        borderRight: "1px solid var(--border-line)",
        background: "var(--bg-1)",
        padding: "16px 12px",
        display: "flex",
        flexDirection: "column",
        gap: 24,
        overflowY: "auto",
      }}
    >
      {NAV.map((group) => (
        <div key={group.group} className="app-stack" style={{ gap: 4 }}>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              letterSpacing: "0.16em",
              color: "var(--fg-3)",
              textTransform: "uppercase",
              padding: "4px 10px",
              marginBottom: 4,
            }}
          >
            {group.group}
          </div>
          {group.items.map((it) => {
            const active = it.key === activeKey;
            return (
              <Link
                key={it.key}
                to={it.href}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "8px 10px",
                  borderRadius: 6,
                  color: active ? "var(--fg-0)" : "var(--fg-1)",
                  background: active ? "var(--bg-2)" : "transparent",
                  border: active
                    ? "1px solid var(--border-line)"
                    : "1px solid transparent",
                  fontFamily: "var(--font-sans)",
                  fontSize: 14,
                  fontWeight: active ? 500 : 400,
                  textDecoration: "none",
                }}
              >
                <span style={{ color: active ? "var(--accent-amber)" : "var(--fg-2)" }}>
                  {it.glyph}
                </span>
                <span style={{ flex: 1 }}>{it.label}</span>
                {it.badge && (
                  <span
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: 11,
                      padding: "1px 6px",
                      background: "var(--accent-amber-12)",
                      color: "var(--accent-amber)",
                      borderRadius: 3,
                    }}
                  >
                    {it.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      ))}

      <div style={{ flex: 1 }} />
    </aside>
  );
}

// ───────────────── Glyphs ─────────────────

function TargetGlyph() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  );
}

function LayersGlyph() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </svg>
  );
}

function ReplayGlyph() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <polyline points="1 4 1 10 7 10" />
      <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
    </svg>
  );
}

function PlayGlyph() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <polygon points="5 3 19 12 5 21 5 3" />
    </svg>
  );
}

function ClipboardGlyph() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
      <rect x="9" y="3" width="6" height="4" rx="1" />
      <line x1="9" y1="12" x2="15" y2="12" />
      <line x1="9" y1="16" x2="15" y2="16" />
    </svg>
  );
}

function ChartGlyph() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 3v18h18" />
      <path d="M7 14l4-4 4 4 5-5" />
    </svg>
  );
}

function PackGlyph() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    </svg>
  );
}

function JournalGlyph() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4H19v14H5.5A1.5 1.5 0 0 0 4 19.5z" />
      <line x1="7.5" y1="8" x2="15.5" y2="8" />
      <line x1="7.5" y1="11" x2="15.5" y2="11" />
      <line x1="7.5" y1="14" x2="12.5" y2="14" />
    </svg>
  );
}

function HelpGlyph() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M9.2 9.3a2.8 2.8 0 0 1 5.3 1.1c0 1.8-2.6 2.3-2.6 4" />
      <line x1="12" y1="17" x2="12" y2="17.01" />
    </svg>
  );
}

function VyzrlyGlyph() {
  // Partner mark keeps its own brand color (not stroke-tinted like the
  // line glyphs), which doubles as an "external app" cue.
  return (
    <img
      src="/logo/vyzrly-mark.png"
      alt=""
      width={16}
      height={16}
      style={{ borderRadius: 3, display: "block" }}
    />
  );
}

function BrandTickSmall() {
  return (
    <svg
      width={22}
      height={22}
      viewBox="0 0 24 24"
      aria-hidden="true"
      stroke="var(--accent-amber)"
      fill="none"
    >
      <circle cx="12" cy="12" r="7" strokeWidth="1.5" />
      <line x1="12" y1="2" x2="12" y2="5" strokeWidth="1.5" />
      <line x1="12" y1="19" x2="12" y2="22" strokeWidth="1.5" />
      <line x1="2" y1="12" x2="5" y2="12" strokeWidth="1.5" />
      <line x1="19" y1="12" x2="22" y2="12" strokeWidth="1.5" />
      <path
        d="M9 12 L11 14 L15 10"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
